import {
  type PartArchiveScope,
  type PartCondition,
  type PartStockGrouping,
  PartStockService,
  type PartStockSort,
  type PartStockStatus,
  type PartStockView,
  type SortDirection,
} from "@common/client"
import { makeGroupPages, makeInfiniteQuery, makeQuery } from "./utils"

const PART_STOCK_PAGE_SIZE = 500

const PART_STOCK_SCROLL_PAGE_SIZE = 50

export type PartStockParams = {
  search: string
  view: PartStockView | undefined
  status: PartStockStatus[] | undefined
  manufacturer: string[] | undefined
  condition: PartCondition[] | undefined
  facilityId: string[] | undefined
  workPackageId: string[] | undefined
  archive: PartArchiveScope
  group: PartStockGrouping | undefined
  subgroup: PartStockGrouping | undefined
  sort: PartStockSort
  direction: SortDirection
}

export const partStockBaseKey = ["part-stock"] as const

export const partStockKey = (partId: string) =>
  [...partStockBaseKey, "detail", partId] as const

export const partStocksKey = (params: PartStockParams) =>
  [...partStockBaseKey, "list", params] as const

function listStock(
  params: PartStockParams,
  skip: number,
  limit: number,
  groupKey: string | null = null,
  groupLimit: number | null = null,
) {
  return PartStockService.listStock({
    search: params.search || null,
    view: params.view ?? null,
    status: params.status ?? null,
    manufacturer: params.manufacturer ?? null,
    condition: params.condition ?? null,
    facility_id: params.facilityId ?? null,
    work_package_id: params.workPackageId ?? null,
    archive: params.archive,
    group: params.group ?? null,
    subgroup: params.subgroup ?? null,
    group_key: groupKey,
    group_limit: groupLimit,
    sort: params.sort,
    direction: params.direction,
    skip,
    limit,
  })
}

export const partStocksQuery = makeQuery({
  serviceCall: (params: PartStockParams) =>
    listStock(params, 0, PART_STOCK_PAGE_SIZE),
  queryKey: partStocksKey,
})

export const partStocksInfiniteQuery = makeInfiniteQuery({
  serviceCall: (params: PartStockParams, { skip, limit }) =>
    listStock(params, skip, limit),
  queryKey: (params) => [...partStockBaseKey, "infinite", params],
  pageSize: PART_STOCK_SCROLL_PAGE_SIZE,
})

export const partStockGroupPages = makeGroupPages({
  serviceCall: (
    params: PartStockParams,
    { skip, limit, group_key, group_limit },
  ) => listStock(params, skip, limit, group_key, group_limit),
  queryKey: (params) => [...partStockBaseKey, "grouped", params],
  pageSize: PART_STOCK_SCROLL_PAGE_SIZE,
})

export const partStocksByNumberQuery = makeQuery({
  serviceCall: (numbers: string[]) =>
    PartStockService.listStock({
      number: numbers,
      limit: PART_STOCK_PAGE_SIZE,
    }),
  queryKey: (numbers) => [...partStockBaseKey, "numbers", numbers],
})

export const partStockQuery = makeQuery({
  serviceCall: PartStockService.get,
  queryKey: ({ part_id, archive }) => [
    ...partStockKey(part_id),
    archive ?? "live",
  ],
})
